import { mergeDefaults as _mergeDefaults, defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createSlots as _createSlots } from "vue"

import {
  BaseFieldEmits,
  defaultValuedProps,
  useBaseFieldProps,
  ValuedFieldProps,
} from 'SharedComponents/Fields/composables/useBaseFieldProps';
import useFieldParams from 'SharedComponents/Fields/composables/useFieldParams';
import { useTooltip } from 'SharedComponents/Fields/composables/useFieldTooltip';
import FieldHistoryBtn from 'SharedComponents/Fields/FieldHistoryBtn.vue';
import SInputDate from 'SharedComponents/ui/Input/SInputDate.vue';
import Tooltip from 'SharedComponents/ui/Tooltip.vue';
import useValidation, { ValidationRule } from 'SharedDir/common/composables/useValidation';
import { EmitValue, PropValue, useValue } from 'SharedDir/common/composables/useValue';
import { DateDirection } from 'SharedDir/constants/field-types';
import { DateFieldParams, FieldValue } from 'SharedDir/declarations/fields';
import {
  getFutureRelativeRestrictionRule,
  getPastRelativeRestrictionRule,
  joinRules,
} from 'SharedDir/utils/validation';
import { computed } from 'vue';

type TextFieldProps = ValuedFieldProps &
  PropValue<FieldValue<null | string>> & {
    params: DateFieldParams;
  };


export default /*@__PURE__*/_defineComponent({
  ...{ name: 'DateField' },
  __name: 'DateField',
  props: /*@__PURE__*/_mergeDefaults({
    readonly: { type: Boolean },
    disable: { type: Boolean },
    required: { type: Boolean },
    useWrapper: { type: Boolean },
    skipValidation: { type: Boolean },
    errorMessage: {},
    label: {},
    showHistory: { type: Boolean },
    history: {},
    modelValue: {},
    params: {}
  }, defaultValuedProps),
  emits: ["update:modelValue", "reveal-history"],
  setup(__props: any, { emit: __emit }) {


const props = __props;
const emit = __emit;
const { LaterThenOrExact, RussianDate, EarlierThenOrExact, EarlierThen, LaterThen } =
  useValidation();

const { innerValue, emitValue } = useValue(props, emit);

const anchorDate = computed(() => {
  if (props.params.restrictionAnchor) {
    return new Date(props.params.restrictionAnchor);
  }
  return new Date();
});

const customRules = computed(() => {
  let rules: Array<ValidationRule> = [RussianDate()];
  const relativeRules: Array<ValidationRule> = [];
  const { pastRestriction: past, futureRestriction: future } = props.params;

  if (props.params.max) {
    // Абсолютные значения всегда "главнее" относительных
    rules.push(EarlierThenOrExact(props.params.max));
  } else if (future) {
    relativeRules.push(getFutureRelativeRestrictionRule(future, anchorDate));
  }

  if (props.params.min) {
    // Абсолютные значения всегда "главнее" относительных
    rules.push(LaterThenOrExact(props.params.min));
  } else if (past) {
    relativeRules.push(getPastRelativeRestrictionRule(past, anchorDate));
  }

  if (past?.direction === DateDirection.after || future?.direction === DateDirection.after) {
    /*
     для случаев когда относительные  ограничения выглядят
     так:
       __________                      ___________________
       \\\\\\\\\\\  [точка отсчета]   ////////////////////
       ___________\________|_________/_____________________>
        after   10 days          10 days  after

     или так:
                    [точка отсчета]
       ____________________|__________
       \\\\\\\\\\\\\\\\\\\\|\\\\\\\\\\\
       ___________\________|___________\____________________>
        after   10 days          10 days  before

     или так:
                    [точка отсчета]
                    _______|________________________________
                   ////////|////////////////////////////////
       ___________/________|___________/____________________>
           before 10 days          10 days after
     Потому что эти правила, примененные по отдельности исключают
     друг друга, и они требуют "объединения" в ИЛИ
     */
    const rule = joinRules(relativeRules, 'or');
    if (rule !== null) {
      rules.push(rule);
    }
  } else {
    /*
    При проверке относительных ограничений И при проверке доступного диапазона дат
    возможно только при случаях когда в обоих правилах(past, future)
    направление(direction) ограничения направлено внутрь(before)
                 [точка отсчета]
                 _______|__________
                ////////|\\\\\\\\\\\
    ___________/________|___________\____________________>
        before 7 days        10 days  before
    в таких случаях необходимо проверять удовлетворения обоих условий (past AND future)
    */
    rules = [...rules, ...relativeRules];
  }

  return rules;
});

const { params, validationRules } = useFieldParams<DateFieldParams>(props, { customRules });

const { validationErrorStatus, hasHistory, revealHistory } = useBaseFieldProps(props, emit);

const tooltip = useTooltip(props);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(SInputDate, {
    ref: "input",
    modelValue: _unref(innerValue).value,
    "onUpdate:modelValue": [
      _cache[1] || (_cache[1] = ($event: any) => ((_unref(innerValue).value) = $event)),
      _unref(emitValue)
    ],
    placeholder: _unref(params).placeholder,
    class: "s-form-input_wrapper",
    required: _ctx.required,
    error: _unref(validationErrorStatus),
    "error-message": _ctx.errorMessage,
    rules: _unref(validationRules),
    label: _ctx.label,
    dense: "",
    clearable: "",
    readonly: _ctx.readonly
  }, _createSlots({ _: 2 }, [
    (_unref(tooltip) || (_ctx.showHistory && _unref(hasHistory)))
      ? {
          name: "prepend",
          fn: _withCtx(() => [
            (_unref(tooltip))
              ? (_openBlock(), _createBlock(Tooltip, {
                  key: 0,
                  text: _unref(tooltip)
                }, null, 8, ["text"]))
              : _createCommentVNode("", true),
            (_ctx.showHistory && _unref(hasHistory))
              ? (_openBlock(), _createBlock(FieldHistoryBtn, {
                  key: 1,
                  history: _ctx.history,
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(revealHistory)(_ctx.label)))
                }, null, 8, ["history"]))
              : _createCommentVNode("", true)
          ]),
          key: "0"
        }
      : undefined
  ]), 1032, ["modelValue", "placeholder", "required", "error", "error-message", "rules", "label", "readonly", "onUpdate:modelValue"]))
}
}

})